export type CoachingSuggestion = {
	// Server-assigned stable id (echoed back on dismiss so it persists across reconnect).
	id: string;
	type: 'anchor_close' | 'objection_reframe' | 'deep_dive_offer' | 'value_prop_pivot' | 'stakeholder_risk' | 'next_step';
	title: string;
	rationale: string;
	suggestedPhrase?: string;
	confidence: 'low' | 'medium' | 'high';
	triggeringQuote: string;
};

export type CopilotUpcomingMeeting = {
	id: number;
	date: string | null;
	subject: string;
	clientName: string | null;
	notetakerWillJoin: boolean;
};

export type CopilotSessionContext = {
	startedAt: number | null;
	deal: { annualValue: number; currency: string | null; stageName: string | null } | null;
	participants: string[];
};

export type CopilotEvent =
	| { type: 'session_started'; appointmentId: number; userId: number; startedAt: number }
	| { type: 'session_ended'; appointmentId: number }
	| { type: 'transcript'; speaker: string; text: string }
	| { type: 'suggestion'; suggestion: CoachingSuggestion }
	| { type: 'ask_question'; id: string; question: string }
	| { type: 'ask_answer_token'; id: string; delta: string }
	| { type: 'ask_answer'; id: string; answer: string }
	| { type: 'error'; error: string };

type ActiveSession = {
	appointmentId: number;
} | null;

class CopilotResource {
	async getActiveSession(): Promise<ActiveSession> {
		const response = await fetch('/api/v2/copilot/active');
		if (!response.ok) return null;
		const json = await response.json();
		return json?.data ?? null;
	}

	streamSession(
		appointmentId: number,
		onEvent: (event: CopilotEvent) => void,
		onError?: (err: Error) => void
	): () => void {
		const eventSource = new EventSource(`/api/v2/copilot/stream/${appointmentId}`);

		eventSource.onmessage = (e) => {
			try {
				const event: CopilotEvent = JSON.parse(e.data);
				onEvent(event);
			} catch (err) {
				console.error('CopilotResource: malformed SSE payload', err);
			}
		};

		eventSource.onerror = () => {
			onError?.(new Error('SSE connection error'));
		};

		return () => {
			eventSource.close();
		};
	}

	async getUpcomingToday(): Promise<CopilotUpcomingMeeting[]> {
		const response = await fetch('/api/v2/copilot/upcoming');
		if (!response.ok) return [];
		const json = await response.json();
		return json?.data ?? [];
	}

	async getSessionContext(appointmentId: number): Promise<CopilotSessionContext | null> {
		const response = await fetch(`/api/v2/copilot/context/${appointmentId}`);
		if (!response.ok) return null;
		const json = await response.json();
		return json?.data ?? null;
	}

	async endSession(appointmentId: number): Promise<void> {
		await fetch(`/api/v2/copilot/end/${appointmentId}`, { method: 'POST' });
	}

	async askCopilot(appointmentId: number, question: string): Promise<void> {
		await fetch('/api/v2/copilot/ask', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ appointmentId, question })
		});
	}

	async insertToNotes(appointmentId: number, text: string): Promise<void> {
		await fetch('/api/v2/copilot/insert-notes', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ appointmentId, text })
		});
	}

	async dismissSuggestion(appointmentId: number, suggestionId: string, reason?: string): Promise<void> {
		await fetch('/api/v2/copilot/dismiss', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ appointmentId, suggestionId, reason })
		});
	}
}

export default new CopilotResource();
